/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import { Button, FontAwesomeIcon, Stack } from "components";
import { ScreenPage } from "templates";
import { useTranslation } from "react-i18next";

import { useUI } from "state/UIContext";
import useRegisterLinkNavigation from "pages/RegisterLink/useRegisterLinkNavigation";
import { useEffect } from "react";
import { getProfile } from "lib/userApi";
import useFetcher from "hooks/useFetcher";
import toast from "react-hot-toast";
import { useInvitation } from "state/InvitationContext";
import useEventRouting from "hooks/useEventRouting";
import { useAuth } from "state/AuthContext";
import ROUTES from "routes";
import useLocalStorage from "utils/useLocalStorage";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import { useOrgData } from "state/OrgDataContext";

import { pushToDataLayer } from "utils/gtm";
const WelcomePage = () => {
  const { t } = useTranslation();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const loginNavigator = useRegisterLinkNavigation();
  const { isLoggedIn } = useAuth();
  const [id] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const { invitation } = useInvitation({
    fetchInvitation: isLoggedIn,
    hardFetch: true,
  });
  const { colorScheme } = useUI();
  const { navigateWithEvent, eventId } = useEventRouting();

  const { data: userData, isLoading } = useFetcher(() =>
    getProfile(id, "user", eventId),
  );

  useEffect(() => {
    if (isLoading)
      toast.loading("Loading...", {
        duration: 500,
      });
  }, [isLoading]);

  const routeToNavigate = () => {
    pushToDataLayer({
      event: "welcome_continue",
      screen_name: "welcome",
      journey_step: "welcome",
      action: "button",
    });
    if (
      isLoggedIn &&
      Object.keys(invitation).length > 0 &&
      !userData.is_form_completed
    ) {
      navigateWithEvent(ROUTES.onboard.invitation);
      return;
    }
    loginNavigator();
  };

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      {!isLoading && (
        <>
          <h2 className="font-bold mb-4 text-3xl md:text-4xl capitalize -text--theme-colors-text-title">
            {userData
              ? t("welcome.dear") +
              " " +
              userData.first_name +
              " " +
              userData.last_name +
              ","
              : t("auth.login.welcomeTitle")}
          </h2>
          <p className="-text--theme-colors-text-body">
            {EVENT_CONTENT.login.selectEventInstruction}
          </p>
          <Stack className="mt-8">
            <Button onClick={routeToNavigate} variant="primary">
              <div className="flex items-center gap-x-s10">
                {t("auth.login.continueButtonLabel")}
                <FontAwesomeIcon name="fa-chevron-right" />
              </div>
            </Button>
          </Stack>
        </>
      )}
    </ScreenPage>
  );
};
export default WelcomePage;
